import type { MeasureShape } from '@aj/core';
import { Button, ChipGroup, Field, HStack, Text, VStack } from '@aj/ui';
import { useEffect, useRef, useState } from 'react';
import type { KeyboardEvent } from 'react';

import { useActions, useLoggedTracks, useSeries } from '../data/hooks.js';
import { useT } from '../providers/I18nProvider.js';
import type { MessageKey } from '@aj/i18n';

/**
 * Recording a number.
 *
 * The shape question is asked ONCE per measure — the first time a name is used
 * — and never again, because the command defines the measure from that first
 * reading. A student who records "Physics mock" every month answers "what is
 * it" once in their life and types a number thereafter.
 *
 * That is what keeps a "manage your metrics" screen from existing. Deciding
 * what to track is system-design work, and system-design work is what students
 * do instead of studying (§45).
 */

const SHAPES: { id: MeasureShape; key: MessageKey; hint: MessageKey }[] = [
  { id: 'outOf', key: 'measure.shapeOutOf', hint: 'measure.shapeOutOfHint' },
  { id: 'rank', key: 'measure.shapeRank', hint: 'measure.shapeRankHint' },
  { id: 'count', key: 'measure.shapeCount', hint: 'measure.shapeCountHint' },
];

export type ReadingFormProps = {
  onDone: () => void;
  /** Links the result to the session that produced it. */
  entryId?: string;
  /** Pre-fills the subject when the caller already knows it. */
  track?: string;
};

export function ReadingForm({ onDone, entryId, track }: ReadingFormProps) {
  const t = useT();
  const actions = useActions();
  const existing = useSeries();
  const tracks = useLoggedTracks();

  const [name, setName] = useState('');
  const [shape, setShape] = useState<MeasureShape>('outOf');
  const [value, setValue] = useState('');
  const [outOf, setOutOf] = useState('');
  const [unit, setUnit] = useState('');
  const [subject, setSubject] = useState(track ?? '');
  const field = useRef<HTMLInputElement>(null);

  useEffect(() => field.current?.focus(), []);

  // A name already in use carries its own definition. Asking again would let a
  // student answer differently and quietly split one series into two.
  const known = existing.find((s) => s.label.toLowerCase() === name.trim().toLowerCase());
  const effectiveShape = known?.shape ?? shape;
  const needsOutOf = effectiveShape === 'outOf' || effectiveShape === 'rank';

  useEffect(() => {
    if (known?.latest.outOf !== undefined) setOutOf(String(known.latest.outOf));
  }, [known?.id, known?.latest.outOf]);

  const numeric = Number(value);
  const total = Number(outOf);
  const ready =
    name.trim().length > 0 &&
    value.trim().length > 0 &&
    Number.isFinite(numeric) &&
    numeric >= 0 &&
    (!needsOutOf || (Number.isFinite(total) && total > 0)) &&
    (effectiveShape !== 'outOf' || numeric <= total);

  const submit = () => {
    if (!ready) return;
    void actions.recordReading({
      measure: name.trim(),
      shape: effectiveShape,
      value: numeric,
      ...(needsOutOf ? { outOf: total } : {}),
      ...(subject.trim() ? { track: subject.trim() } : {}),
      ...(effectiveShape === 'count' && unit.trim() ? { unit: unit.trim() } : {}),
      ...(entryId === undefined ? {} : { entryId }),
    });
    onDone();
  };

  const keys = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') submit();
  };

  return (
    <VStack gap={4}>
      <VStack gap={2}>
        <Text variant="overline" tone="subtle">
          {t('measure.name')}
        </Text>
        <Field
          inputRef={field}
          value={name}
          onChange={setName}
          onKeyDown={keys}
          label={t('measure.name')}
          placeholder={t('measure.namePlaceholder')}
          className="w-full"
        />
      </VStack>

      {/* Only for a name the app has never seen. After that the measure knows
          what it is and the question never returns. */}
      {!known && name.trim().length > 0 && (
        <VStack gap={2}>
          <Text variant="overline" tone="subtle">
            {t('measure.shape')}
          </Text>
          <ChipGroup
            label={t('measure.shape')}
            options={SHAPES.map((option) => ({ value: option.id, label: t(option.key) }))}
            value={shape}
            onSelect={(id) => setShape(id as MeasureShape)}
          />
          <Text variant="caption" tone="subtle">
            {t(SHAPES.find((option) => option.id === shape)?.hint ?? 'measure.shapeOutOfHint')}
          </Text>
        </VStack>
      )}

      <VStack gap={2}>
        <Text variant="overline" tone="subtle">
          {t('measure.value')}
        </Text>
        <HStack gap={2} align="center">
          <Field
            numeric
            value={value}
            onChange={(next) => setValue(next.replace(/[^\d]/g, ''))}
            onKeyDown={keys}
            label={t('measure.value')}
            className="w-24 flex-none tabular-nums"
          />
          {needsOutOf && (
            <>
              <Text variant="body" tone="subtle">
                {t('measure.outOf')}
              </Text>
              <Field
                numeric
                value={outOf}
                onChange={(next) => setOutOf(next.replace(/[^\d]/g, ''))}
                onKeyDown={keys}
                // Pre-filled from the last reading, because the total usually
                // repeats — but selected on focus, so the month it doesn't the
                // student types over it instead of deleting three digits first.
                onFocus={(event) => event.currentTarget.select()}
                label={t('measure.outOf')}
                className="w-24 flex-none tabular-nums"
              />
            </>
          )}
          {!known && effectiveShape === 'count' && (
            <Field
              value={unit}
              onChange={setUnit}
              onKeyDown={keys}
              label={t('measure.unit')}
              placeholder={t('measure.unitPlaceholder')}
              className="w-32 flex-none"
            />
          )}
        </HStack>
      </VStack>

      {/* Only a result on a subject may ever say the student is improving
          (§53.8), so this is the field that unlocks that — and it stays
          optional, because a page count belongs to no subject. */}
      {!known && tracks.length > 0 && (
        <VStack gap={2}>
          <Text variant="overline" tone="subtle">
            {t('measure.track')}
          </Text>
          <ChipGroup
            label={t('measure.track')}
            options={tracks.map((option) => ({ value: option, label: option }))}
            value={subject}
            onSelect={setSubject}
            clearable
          />
        </VStack>
      )}

      <Button icon="check" fullWidth disabled={!ready} onClick={submit}>
        {t('measure.save')}
      </Button>
    </VStack>
  );
}
